import { getFormDataFromState } from 'AppSrc/core/components/forms/form-types/create-crq/get-create-crq-parameters';
import { COORDINATOR, RAISING_CRQ } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import {
  currentSelectedNetwork,
  crqRequiresPou,
  getPouId,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import { CREATE_CRQ } from 'AppSrc/core/components/create-crq-screen/create-crq-screen-consts';
import { userRolesSelector } from 'AppSrc/core/auth/auth-selectors';
import { getCrq } from 'AppSrc/core/crq/crq-queries';
import { getFormFlowForEditingStatus } from 'AppSrc/core/components/forms/form-section-utility-functions';
import { FIXED } from 'AppSrc/core/components/forms/value-dependency-constants';


const REFRESH_POU_REQUEST = 'REFRESH_POU_REQUEST';
const REFRESH_POU_ERROR = 'REFRESH_POU_ERROR';
const REFRESH_POU_SUCCESS = 'REFRESH_POU_SUCCESS';
const SET_POU_MAIN_FORM = 'SET_POU_MAIN_FORM';

const setPouMainForm = (formId, pou) => ({
  type: SET_POU_MAIN_FORM,
  payload: {
    formId,
    pou,
  },
});

const refreshPouRequest = () => ({
  type: REFRESH_POU_REQUEST,
  payload: {},
});

const refreshPouSuccess = () => ({
  type: REFRESH_POU_SUCCESS,
  payload: {},
});

const refreshPouError = (error) => ({
  type: REFRESH_POU_ERROR,
  payload: {
    error,
  },
});

// We should fire off a request to retrieve the POU for a CRQ if:
// - we are raising a fixed CRQ as coordinator
// - and the crq has the correct impact level
// - and the crq does not already have a value for POU
const shouldRefreshPou = ({
  userRoles,
  formData,
  network,
  editingStatus,
}) => {
  // only fixed CRQs have POU ids
  if (network !== FIXED) return false;

  const formFlow = getFormFlowForEditingStatus(editingStatus);
  // we should only refresh the POU from the server if raising a CRQ
  if (formFlow !== RAISING_CRQ) return false;

  // only refresh POU if current logged in user is a coordinator
  const userIsCoordinator = userRoles[network] === COORDINATOR;
  if (!userIsCoordinator) return false;

  // only refresh POU if this CRQ requires a POU
  const requiresPou = crqRequiresPou(formData);
  if (!requiresPou) return false;

  // we do not need to refresh the POU if there is already a value
  const currentPou = getPouId(formData);
  if (currentPou) return false;

  return true;
};

// Thunk action to refresh the redux value of POU with any generated value on the server.
// When creating a CRQ, there is a remedy background process that generates a POU id. We
// have no way of telling when it will be generated, but the UI should show the POU id if
// it is available. This action is triggered during raise CRQ on both the references and summary
// steps when the step is loaded. The action first checks if the CRQ should have a POU (based on
// impact level) and then if there is no POU, it calls getCrq and populates any generated value
// in the redux form data.
// this is only necessary on raise CRQ, as the view CRQ flow calls getCrq on each step
const refreshPouAction = (editingStatus) => (dispatch, getState) => {
  const state = getState();
  const userRoles = userRolesSelector(state);
  const formData = getFormDataFromState(state);
  const network = currentSelectedNetwork(formData);
  // only refresh if necessary
  const shouldRefresh = shouldRefreshPou({
    userRoles,
    network,
    formData,
    editingStatus,
  });
  if (!shouldRefresh) return;
  const crqId = formData.crqId;
  // call getCrq api and retrieve the POU
  dispatch(refreshPouRequest());
  getCrq(network, crqId).then(({ crq }) => {
    const { pou } = crq;
    if (pou) {
      dispatch(setPouMainForm(CREATE_CRQ, pou));
    }
    dispatch(refreshPouSuccess());
  }).catch((error) => {
    dispatch(refreshPouError(error));
  });
};

export {
  setPouMainForm,
  SET_POU_MAIN_FORM,
  REFRESH_POU_REQUEST,
  REFRESH_POU_ERROR,
  REFRESH_POU_SUCCESS,
  refreshPouAction,
};
